import { CustomRowConfiguration, RowsConfiguration } from '@mailupinc/bee-plugin/dist/types/bee';
import { MutableRefObject, RefObject } from 'react';
import { match, P } from 'ts-pattern';

import { pluralizeWord } from 'modules/campaigns/lib/utils';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { CustomFont } from 'modules/templateEditor/context/ContentEditorPreferencesContext';
import { isHtmlElementNode, isImageElement } from 'utils/elementHelpers';
import { NoOp } from 'utils/noop';

import {
  ALL_SAVED_ROWS_CATEGORY,
  CATEGORY_LIMIT_ERROR,
  CREATE_SAVED_ROW_ERROR_MESSAGE,
  DEFAULT_BEE_PLUGIN_CONFIG,
  DEFAULT_SAVED_ROW_PAGE_CONTAINER_CONFIG,
  DEFAULT_SAVED_ROWS_CATEGORY_VALUES,
  DELETE_SAVED_ROW_ERROR_MESSAGE,
  HANDLEBARS_HELPERS,
  MY_SAVED_ROWS_CATEGORY,
  PRESET_SAVED_ROWS_CATEGORIES,
} from './constants';
import { QuerySavedRowsVariables } from './hooks';
import { UpdateSavedRowVariables } from './hooks/useUpdateSavedRowMutation';
import {
  AddOn,
  AuthToken,
  BeeConfig,
  BeeSavedRowMutationFailure,
  ContentDialogHandler,
  ContentDialogHandlerMapping,
  ContentModule,
  ContentModuleType,
  HtmlContentModule,
  ImageContentModule,
  MessageCategory,
  MixedContentModule,
  ParagraphContentModule,
  SavedRow,
  SavedRowsConfig,
} from './types';

export const isAuthTokenDefined = (authToken?: AuthToken) => {
  return !!authToken?.access_token;
};

export const isAuthTokenUnexpired = (authToken?: AuthToken) => {
  if (!authToken) return false;
  // check for expired token
  const exp = new Date(authToken['.expires']);
  const now = new Date();
  return exp > now;
};

export const getEditorFonts = (customFonts: CustomFont[] = []): BeeConfig['editorFonts'] => {
  return {
    customFonts,
    showDefaultFonts: true,
  };
};

export const getMergeTags = (mergeParams: string[] = []) =>
  mergeParams
    .map((mergeParam) => ({
      name: mergeParam,
      value: `{{${mergeParam}}}`,
    }))
    .concat(HANDLEBARS_HELPERS);

/**
 * Generates BEE Plugin configuration object
 * @param customFonts
 * @param hooks
 * @param mergeParams
 * @param onBeeChangeRef
 * @param onBeeSaveCallbackRef
 * @param onEditorLoadCallbackRef
 * @param uid
 * @param advancedPermissions
 * @param contentDialog
 * @param addOns
 * @param savedRowsConfig
 *
 * @see https://docs.beefree.io/configuration-parameters/
 * @see https://docs.beefree.io/font-management/
 */
export const getEditorConfig = (
  customFonts: CustomFont[] = [],
  hooks = {},
  mergeParams: string[] = [],
  // important: use a MutableRefObject so the callback stays current in the EditorConfig
  // - otherwise you may be updating with reference to a stale callback - this is especially
  //   relevant in template editing where the callback contains information related to the locale
  //   being edited
  onBeeChangeRef: MutableRefObject<(json: string) => void>,
  onBeeSaveCallbackRef: MutableRefObject<(json: string, html: string) => void>,
  onEditorLoadCallbackRef: MutableRefObject<() => void>,
  uid: string,
  advancedPermissions: BeeConfig['advancedPermissions'] = {},
  contentDialog: BeeConfig['contentDialog'] = {},
  addOns: AddOn[] = [],
  savedRowsConfig?: SavedRowsConfig,
): BeeConfig => {
  const mergeTags = getMergeTags(mergeParams);
  const editorFonts = getEditorFonts(customFonts);

  return {
    ...DEFAULT_BEE_PLUGIN_CONFIG,
    ...savedRowsConfig,
    hooks,
    editorFonts,
    advancedPermissions,
    contentDialog,
    mergeTags,
    uid, // Users can share images within same project,
    // @ts-ignore - TODO: fix type error
    addOns,
    // note: the json in onAutoSave and onChange is different than that returned by
    // onSave - the onSave version contains additional keys such as UUID
    // onAutoSave: (json: string) => onBeeChangeRef.current(json),
    onChange: (json: string) => onBeeChangeRef.current(json),
    onSave: (json: string, html: string) => onBeeSaveCallbackRef.current(json, html),
    onLoad: () => onEditorLoadCallbackRef.current(),
  };
};

export const createCurrentImgElt = (argUrl: string) => {
  // argUrl = "url('[imageUrl]')"
  const url = (argUrl?.match(/^url\('(.+)'\)$/) || [])[1]; // the inner url string or undefined
  if (!url) {
    return undefined;
  }
  const eltContainer = document.createElement('div');
  eltContainer.innerHTML = `<img src="${url}" />`;

  return eltContainer.childNodes[0] as HTMLImageElement;
};

export const createCurrentElt = (tag: string, attrs: Record<string, string>) => {
  const attrStr = Object.keys(attrs)
    .reduce((attrPairs: string[], attrKey: string) => {
      // @ts-ignore
      const attrValue = attrs[attrKey].trim().replaceAll('"', '&quot;');
      if (attrValue) {
        attrPairs.push(`${attrKey}="${attrValue}"`);
      }
      return attrPairs;
    }, [])
    .join(' ');
  const eltContainer = document.createElement('div');
  eltContainer.innerHTML = `<${tag} ${attrStr} />`;

  return eltContainer.childNodes[0] as HTMLImageElement;
};

/**
 * Creates a handler that shows the correct content dialog when its corresponding
 * content block is edited in the BEE editor. Pass in a mapping between custom BEE
 * add-on IDs (set in the BEE SDK console) and the code that you want to run with it.
 */
export const createAddOnContentDialogHandler =
  (contentDialogIdsToHandlers: ContentDialogHandlerMapping) =>
  (resolve: (obj: Record<string, any>) => void, reject: NoOp, args: Record<string, any>) => {
    const handler: ContentDialogHandler | undefined =
      contentDialogIdsToHandlers[args.contentDialogId];

    if (handler) {
      handler(resolve, reject, args);
    } else {
      reject();
    }
  };

export const createHtmlContentModule = (html: string): HtmlContentModule => ({
  type: ContentModuleType.html,
  value: {
    html,
  },
});

export const createParagraphContentModule = (html: string): ParagraphContentModule => ({
  type: ContentModuleType.paragraph,
  value: {
    html,
  },
});

export const createImageContentModule = (src: string): ImageContentModule => ({
  type: ContentModuleType.image,
  value: {
    src,
  },
});

// https://docs.beefree.io/beefree-sdk/builder-addons/addons/custom-addons/addon-development#mixed-content-addons
export const createMixedContentModule = (html: string): MixedContentModule => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const value: ContentModule[] = [];

  doc.body.childNodes.forEach((node) => {
    if (!isHtmlElementNode(node)) {
      value.push(createParagraphContentModule(node.textContent ?? ''));
    } else if (isImageElement(node)) {
      value.push(createImageContentModule(node.src));
    } else {
      value.push(createParagraphContentModule(node.innerHTML));
    }
  });

  return {
    type: ContentModuleType.mixed,
    value,
  };
};

export const getCustomRowConfigurations = (
  messageCategories: MessageCategory[] = [],
): CustomRowConfiguration[] => {
  return messageCategories.map(({ id, name }) => ({
    name,
    value: id?.toString(),
    handle: id?.toString(),
    ...DEFAULT_SAVED_ROWS_CATEGORY_VALUES,
  }));
};

export const getRowsConfiguration = (
  messageCategories: MessageCategory[] = [],
): RowsConfiguration => ({
  emptyRows: true,
  defaultRows: false,
  externalContentURLs: [
    ...PRESET_SAVED_ROWS_CATEGORIES,
    ...getCustomRowConfigurations(messageCategories),
  ],
});

export const getSavedRowsConfig = (
  isSavedRowsFeatureEnabled: boolean,
  messageCategories: MessageCategory[] = [],
  onSaveRowCallbackRef: RefObject<(beeBuilderMetadata: string, rowHTML: string) => void>,
): SavedRowsConfig => {
  return isSavedRowsFeatureEnabled
    ? {
        saveRows: true,
        rowsConfiguration: getRowsConfiguration(messageCategories),
        onSaveRow: (rowJSON: string, rowHTML: string) => {
          const { metadata, ...beeBuilderMetadata } = JSON.parse(rowJSON) ?? {}; // don't save the metadata so it does not go stale. we instead generate it on the fly with the latest data.

          onSaveRowCallbackRef.current?.(JSON.stringify(beeBuilderMetadata), rowHTML);
        },
      }
    : // the code below helps mirror the current production
      // drag and drop experience for any saved rows-related
      // UI. more specifically, for projects on an instance
      // which has `Save rows` enabled in Beefree SDK console
      // BUT without the SavedRows feature flag on, the save row
      // icon should NOT appear and the rows tab should only
      // display empty rows without a categories dropdown
      {
        saveRows: false,
        rowsConfiguration: {
          emptyRows: true,
        },
      };
};

export const filterDeletedRow = (savedRows: SavedRow[], deletedRowId: number | null) =>
  savedRows?.filter((r) => r.id !== deletedRowId) ?? [];

/*
 * Get the template json for editing a single saved row
 * NOTE: In the future, we may want to consider storing the page metadata instead of generating it on the fly.
 * In fact, this is recommended by BEE (see https://iterable.slack.com/archives/C03052SB1FC/p1721776866850159, Jira ticket: https://iterable.atlassian.net/browse/CAMP-2925).
 */
export const getPageBeeBuilderMetadata = (savedRow: SavedRow) => {
  const rowBeeBuilderMetadata = savedRow?.primaryContent?.beeBuilderMetadata || '{}';
  const row = JSON.parse(rowBeeBuilderMetadata);

  const template = {
    ...DEFAULT_SAVED_ROW_PAGE_CONTAINER_CONFIG,
    page: { ...DEFAULT_SAVED_ROW_PAGE_CONTAINER_CONFIG.page, rows: [row] },
  };

  return JSON.stringify(template);
};

/**
 * Converts Iterable saved rows to bee saved rows
 */
export const toBeeSavedRows = (savedRows: SavedRow[]) => {
  return (
    savedRows?.map((savedRow) => {
      const beeBuilderMetadata = JSON.parse(savedRow?.primaryContent?.beeBuilderMetadata ?? '{}');
      // we are generating the metadata with the saved row data on the fly to make sure it is always up to date
      const metadata = {
        name: savedRow?.name,
        id: savedRow?.id,
        category: { id: savedRow?.category?.id ?? -1, name: savedRow?.category?.name },
      };

      return { ...beeBuilderMetadata, metadata };
    }) ?? []
  );
};

/**
 * Generates filter variables for the currently selected saved rows category
 */
export const getSavedRowsFilterVariables = (
  categoryValue: string,
  createdByUserId?: string,
): QuerySavedRowsVariables => {
  switch (categoryValue) {
    case ALL_SAVED_ROWS_CATEGORY.value:
      return {};
    case MY_SAVED_ROWS_CATEGORY.value:
      return { filterInfo: { createdByUserId } };
    default:
      return { filterInfo: { categoryId: categoryValue ? parseInt(categoryValue, 10) : -1 } };
  }
};

export const getCategoryOptions = (messageCategories: MessageCategory[]) =>
  messageCategories.map((category) => {
    const rowCount = category.rowsCount;

    return {
      label: category.name,
      value: `${category.id}`,
      Suffix: `${rowCount} ${pluralizeWord('row', rowCount)}`,
    };
  });

export const pluralizeCategory = <Categories extends any[]>(categories: Categories) => {
  return pluralizeWord('category', categories?.length, 'categories');
};

export const createSavedRowFailureToErrorMessage = (failure: BeeSavedRowMutationFailure) => {
  return (
    match(failure)
      // row errors
      .with(
        {
          __typename: GRAPHQL_TYPENAMES.BEE_ROW_NAME_ALREADY_EXISTS_FAILURE,
        },
        () => 'Row name already exists.',
      )
      .with(
        {
          __typename: GRAPHQL_TYPENAMES.BEE_CREATE_FIELD_TOO_LONG_FAILURE,
        },
        () => 'Row name exceeds limit of 180 characters.',
      )
      .with(
        {
          __typename: GRAPHQL_TYPENAMES.BEE_CREATE_FIELD_NOT_PROVIDED,
        },
        () => 'Row name not provided.',
      )
      // category errors
      .with(
        {
          __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_MAXIMUM_COUNT_EXCEEDED_FAILURE,
        },
        () => CATEGORY_LIMIT_ERROR,
      )
      .with(
        {
          __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_UPDATE_FAILURE,
        },
        () => 'Failed to update category.',
      )
      .with(
        {
          __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_CREATION_FAILURE,
        },
        () => 'Failed to create category.',
      )
      .with(
        {
          __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_TOO_LONG_FAILURE,
        },
        () => 'Category name exceeds limit of 40 characters.',
      )
      .otherwise(() => CREATE_SAVED_ROW_ERROR_MESSAGE)
  );
};

export const deleteSavedRowFailureToErrorMessage = (failure: BeeSavedRowMutationFailure) => {
  return match(failure)
    .with(
      {
        errorMsg: P.string.startsWith('Invalid message category ids given for delete'),
      },
      () => 'Unable to delete saved row. The row may already have been deleted.',
    )
    .otherwise(() => DELETE_SAVED_ROW_ERROR_MESSAGE);
};

/**
 * Generates GraphQL variables for updating a saved row
 *
 * @param savedRow The saved row being updated
 * @param pageHTML The updated HTML content
 * @param pageJSON The page metadata JSON with the updated row within it
 */
export const getUpdateSaveRowDesignVariables = (
  savedRow: SavedRow,
  pageHTML: string,
  pageJSON: string,
): UpdateSavedRowVariables => {
  const pageBeeBuilderMetadataObject = JSON.parse(pageJSON);

  const { metadata, ...beeBuilderRowMetadataObj } =
    pageBeeBuilderMetadataObject?.page?.rows?.[0] ?? {};

  return {
    rowId: savedRow.id as number,
    rowName: savedRow.name as string,
    categoryId: savedRow.category?.id as number,
    content: beeBuilderRowMetadataObj && {
      html: pageHTML,
      beeBuilderMetadata: JSON.stringify(beeBuilderRowMetadataObj),
    },
  };
};
